import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../core/auth/auth.service';
import {
  ACTION_STATUS_CLASS, ACTION_STATUS_LABELS, ACTION_STATUS_OPTIONS,
} from '../../core/occurrence/occurrence.labels';
import { OccurrenceAction, OccurrenceActionStatus } from '../../core/occurrence/occurrence.models';
import { OccurrenceService } from '../../core/occurrence/occurrence.service';
import { ToastService } from '../../core/ui/toast.service';
import { ConfirmDialog } from '../confirm-dialog/confirm-dialog';

/**
 * As ações do tratamento (documento de 2026-10-03). Era um campo de texto com um responsável
 * para tudo; é uma lista, cada ação com o seu número, o seu texto e o seu responsável.
 *
 * <p>Vive em dois sítios — o cartão da ficha e o separador Tratamento do formulário —, como o
 * seguimento: um componente só evita que os dois acabem a oferecer coisas diferentes.
 */
@Component({
  selector: 'app-occurrence-actions',
  imports: [FormsModule, ConfirmDialog],
  templateUrl: './occurrence-actions.html',
})
export class OccurrenceActions {
  private readonly service = inject(OccurrenceService);
  private readonly toasts = inject(ToastService);
  protected readonly auth = inject(AuthService);

  readonly occurrenceId = input.required<number>();
  /** Quem pode ficar responsável — a mesma lista do resto da ocorrência. */
  readonly teamMembers = input<readonly { id: number; name: string }[]>([]);
  /** Alguma coisa mudou: quem aloja recarrega o que depende das ações. */
  readonly changed = output<void>();

  protected readonly actions = signal<OccurrenceAction[]>([]);
  protected readonly error = signal<string | null>(null);
  protected readonly saving = signal(false);
  /** Null quando fechado, 0 quando é uma ação nova, o id quando se está a corrigir uma. */
  protected readonly editing = signal<number | null>(null);
  protected readonly pendingRemoval = signal<OccurrenceAction | null>(null);
  protected readonly canWrite = computed(() => this.auth.can('occurrence:write'));

  readonly count = computed(() => this.actions().length);

  /** O estado da ação (revisão de 2026-10-19): muda-se aqui na edição ou direto na tabela. */
  protected readonly statusOptions = ACTION_STATUS_OPTIONS;
  protected readonly statusLabels = ACTION_STATUS_LABELS;
  protected readonly statusClass = ACTION_STATUS_CLASS;

  protected description = '';
  protected responsibleId: number | null = null;
  protected status: OccurrenceActionStatus = 'REGISTADA';

  constructor() {
    // O load fica fora do rastreio: lê o tenant da sessão, e ao sair a sessão esvaziava-se e o
    // efeito repetia os pedidos para /t/null.
    effect(() => {
      this.occurrenceId();
      untracked(() => this.load());
    });
  }

  /** Volta a ler — a ação pode ter mudado noutro sítio (o painel de edição, por cima da ficha). */
  reload(): void {
    this.load();
  }

  private load(): void {
    this.service.actions(this.occurrenceId()).subscribe({
      next: (list) => this.actions.set(list),
      error: () => this.actions.set([]),
    });
  }

  protected openNew(): void {
    this.description = '';
    this.responsibleId = null;
    this.status = 'REGISTADA';
    this.error.set(null);
    this.editing.set(0);
  }

  protected openEdit(action: OccurrenceAction): void {
    this.description = action.description;
    this.responsibleId = action.responsibleId;
    this.status = action.status;
    this.error.set(null);
    this.editing.set(action.id);
  }

  protected cancel(): void {
    this.editing.set(null);
  }

  protected save(): void {
    if (!this.description.trim()) {
      this.error.set('Descreva a ação.');
      return;
    }
    const id = this.editing();
    if (id === null) return;
    this.error.set(null);
    this.saving.set(true);
    const payload = {
      description: this.description.trim(), responsibleId: this.responsibleId, status: this.status,
    };
    const request = id === 0
      ? this.service.addAction(this.occurrenceId(), payload)
      : this.service.updateAction(this.occurrenceId(), id, payload);
    request.subscribe({
      next: () => {
        this.saving.set(false);
        this.editing.set(null);
        this.load();
        this.changed.emit();
        this.toasts.success(id === 0 ? 'Ação acrescentada.' : 'Ação atualizada.');
      },
      error: () => {
        this.saving.set(false);
        this.error.set('Não foi possível guardar a ação.');
      },
    });
  }

  /**
   * Sobe ou desce uma ação na lista (revisão de 2026-10-13: "deve ser possível movimentar a
   * ordem das ações após registo"). Muda-se no ecrã logo, e grava-se a ordem inteira; se a
   * gravação falhar, volta a ler-se a que ficou no servidor.
   *
   * <p>O número não acompanha: é por ele que o seguimento e as notas citam cada ação.
   */
  protected move(index: number, offset: -1 | 1): void {
    const list = [...this.actions()];
    const target = index + offset;
    if (target < 0 || target >= list.length) return;
    this.error.set(null);
    [list[index], list[target]] = [list[target], list[index]];
    this.actions.set(list);
    this.service.reorderActions(this.occurrenceId(), list.map((action) => action.id)).subscribe({
      next: () => this.changed.emit(),
      error: () => {
        this.error.set('Não foi possível mudar a ordem das ações.');
        this.load();
      },
    });
  }

  /**
   * Muda o estado direto na tabela, sem abrir a edição. Muda-se no ecrã logo; se a gravação
   * falhar, volta a ler-se o que ficou no servidor.
   */
  protected changeStatus(action: OccurrenceAction, status: OccurrenceActionStatus): void {
    if (status === action.status) return;
    this.error.set(null);
    this.actions.update((list) => list.map((item) => item.id === action.id ? { ...item, status } : item));
    this.service.updateAction(this.occurrenceId(), action.id, {
      description: action.description, responsibleId: action.responsibleId, status,
    }).subscribe({
      next: () => this.changed.emit(),
      error: () => {
        this.error.set('Não foi possível mudar o estado da ação.');
        this.load();
      },
    });
  }

  protected askRemove(action: OccurrenceAction): void {
    this.pendingRemoval.set(action);
  }

  protected cancelRemove(): void {
    this.pendingRemoval.set(null);
  }

  protected confirmRemove(): void {
    const action = this.pendingRemoval();
    if (!action) return;
    this.pendingRemoval.set(null);
    this.service.deleteAction(this.occurrenceId(), action.id).subscribe({
      next: () => {
        this.load();
        this.changed.emit();
        this.toasts.success('Ação removida.');
      },
      // A recusa diz porquê: a última ação não sai com seguimento registado (2026-10-19).
      error: (error) => this.error.set(error?.error?.message ?? 'Não foi possível remover a ação.'),
    });
  }
}
